import * as Sentry from "@sentry/react";
import { getConsent } from "@/shell/CookieConsent";

function envVar(key: string): string | undefined {
  const val = import.meta.env[key] as string | undefined;
  // Vite's import.meta.env stringifies everything — treat "undefined" as absent.
  return val === undefined || val === "undefined" || val === "" ? undefined : val;
}

let sentryStarted = false;

/**
 * Idempotent. Initialises Sentry the first time it's called and noops on
 * subsequent calls. Returns true if Sentry is now (or already was) running.
 */
export function initSentry(): boolean {
  if (sentryStarted) return true;
  const dsn = envVar("VITE_SENTRY_DSN");
  if (!dsn) return false;
  Sentry.init({
    dsn,
    release: envVar("VITE_GIT_SHA") ?? "unknown",
    environment: "production",
    tracesSampleRate: 0,
  });
  sentryStarted = true;
  return true;
}

/**
 * Initialise Sentry only if the user has accepted optional cookies. Safe
 * to call at app boot — on first visit (consent === "unknown") this is a
 * noop; the CookieConsent banner re-invokes initSentry() directly on its
 * Accept handler so telemetry starts without a page reload.
 */
export function maybeInitSentry(): boolean {
  if (getConsent() !== "accepted") return false;
  return initSentry();
}

/**
 * Test-only: resets the module-level "already started" flag so each test
 * can call initSentry() and observe the underlying @sentry/react.init
 * call without interference from a prior test in the same file.
 */
export function __resetForTests(): void {
  sentryStarted = false;
}
